import { useState } from 'react';
import { cn } from '@/lib/utils';
import { TRADING_CARD_GAMES } from '@/constants/tradingCardGames';

interface Props {
  value: string; // 'all' or a game value
  onChange: (value: string) => void;
  /** Optional whitelist of game values that actually exist in the dataset. */
  availableGames?: string[];
  className?: string;
}

// Most-used games surfaced by default. Everything else lives under "More games".
const TOP_GAMES = ['pokemon', 'mtg', 'onepiece', 'yugioh', 'lorcana', 'gundam', 'fab', 'dragonball', 'riftbound'];

export function GameChipsFilter({ value, onChange, availableGames, className }: Props) {
  const [expanded, setExpanded] = useState(false);

  const all = TRADING_CARD_GAMES.filter((g) =>
    availableGames ? availableGames.includes(g.value) : true,
  ).map((g) => ({ value: g.value, label: g.label }));

  const topSet = new Set(TOP_GAMES);
  const top = all.filter((g) => topSet.has(g.value));
  const rest = all.filter((g) => !topSet.has(g.value));

  // Always show the currently-selected chip even if it's in the hidden tail.
  const selectedInRest = !expanded && value !== 'all' && rest.find((g) => g.value === value);
  const visible = [
    { value: 'all', label: 'All games' },
    ...top,
    ...(selectedInRest ? [selectedInRest] : []),
    ...(expanded ? rest.filter((g) => g.value !== selectedInRest?.value) : []),
  ];

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {visible.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex items-center min-h-9 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all border',
              active
                ? 'bg-active-gold/15 text-active-gold border-active-gold/50 font-medium shadow-[0_0_0_1px_hsl(var(--active-gold)/0.15)]'
                : 'bg-white/[0.05] text-foreground/85 border-white/[0.06] hover:bg-white/[0.08] hover:text-foreground active:scale-[0.97]',
            )}
          >
            {opt.label}
          </button>
        );
      })}
      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center min-h-9 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all bg-white/[0.03] text-muted-foreground border border-white/[0.06] hover:bg-white/[0.08] hover:text-foreground active:scale-[0.97]"
        >
          {expanded ? 'Show less' : 'More games'}
        </button>
      )}
    </div>
  );
}
